import { supabase } from './supabase';
import { functionErrorMessage } from './functionError';
import type { Workspace, WorkspaceState } from '../types/database';

// Select these for the active workspace: its row plus the state and limits the database
// works out from it.
export const WORKSPACE_FIELDS = '*, state, comped, prompt_limit, team_member_limit';

export const STATE_LABELS: Record<WorkspaceState, string> = {
  trial: 'Trial',
  awaiting_payment: 'Awaiting payment',
  paid: 'Paid',
  lapsed: 'Lapsed',
  cancelled: 'Cancelled',
};

// The comped plan while a comp is in force, otherwise the workspace's plan.
export const planInEffect = (workspace: Workspace | null | undefined) =>
  (workspace?.comped && workspace.comped_plan) || workspace?.plan || 'free';

// Lapsed or Cancelled: the database hides its data and refuses new collection.
export const isLocked = (workspace: Workspace | null | undefined) =>
  workspace?.state === 'lapsed' || workspace?.state === 'cancelled';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// The Trial countdown, from the Trial end date the database set.
export function trialCountdown(workspace: Workspace | null | undefined) {
  const end = workspace?.trial_ends_at ? Date.parse(workspace.trial_ends_at) : NaN;
  const start = workspace?.created_at ? Date.parse(workspace.created_at) : NaN;
  if (Number.isNaN(end) || Number.isNaN(start)) return { daysLeft: 0, totalDays: 0 };
  return {
    daysLeft: Math.max(0, Math.ceil((end - Date.now()) / MS_PER_DAY)),
    totalDays: Math.round((end - start) / MS_PER_DAY),
  };
}

// Deletes a workspace and makes another of the user's workspaces active, or none.
// Returns the workspace that's now active.
export async function deleteWorkspace(userId: string, workspaceId: string) {
  const { data: remaining } = await supabase
    .from('workspace_members')
    .select('workspace_id, workspaces:workspace_id(id, name)')
    .eq('user_id', userId)
    .neq('workspace_id', workspaceId)
    .limit(1);

  const nextWorkspace = remaining && remaining.length > 0 ? (remaining[0] as any).workspaces : null;

  const { data, error } = await supabase.functions.invoke('delete-workspace', {
    body: { userId, workspaceId },
  });

  if (error) throw new Error(await functionErrorMessage(error));
  if (!data?.success) {
    throw new Error(data?.error || 'Failed to delete workspace');
  }

  await supabase
    .from('profiles')
    .update(
      nextWorkspace?.id
        ? { workspace_id: nextWorkspace.id, onboarding_completed: true }
        : { workspace_id: null, onboarding_completed: false }
    )
    .eq('id', userId);

  return nextWorkspace;
}
